import { useEffect, useMemo, useState } from "react";
import { Mail, Search } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import StatCard from "@/components/admin/StatCard";
import { adminApi } from "@/lib/adminApi";
import { useT } from "@/i18n/LanguageContext";

/**
 * /admin/messages — Share Your Thoughts inbox.
 *
 * Reads from GET /api/admin/share-thoughts (already exists) and renders a
 * sortable, searchable list. Reply UI is deferred to a later phase.
 */

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

export default function AdminMessages() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await adminApi.listMessages();
        if (!cancelled) setItems(d.items || []);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter((m) =>
      (m.name || "").toLowerCase().includes(s) ||
      (m.email || "").toLowerCase().includes(s) ||
      (m.message || "").toLowerCase().includes(s),
    );
  }, [items, q]);

  const total = items.length;
  const weekAgo = Date.now() - 7 * 86400 * 1000;
  const recent = items.filter((m) => {
    const created = m.created_at ? new Date(m.created_at).getTime() : 0;
    return created >= weekAgo;
  }).length;

  return (
    <AdminShell title={t("admin.messages.title")}>
      <p className="text-sm text-bone/60 -mt-6 mb-8 max-w-2xl">
        {t("admin.messages.intro")}
      </p>

      <section className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8" data-testid="messages-stats">
        <StatCard testId="msg-total" icon={Mail} label={t("admin.messages.stats.total")} value={total} loading={loading} />
        <StatCard testId="msg-recent" icon={Mail} label={t("admin.messages.stats.recent")} value={recent} hint={t("admin.label.rollingWeek")} accent="green" loading={loading} />
        <StatCard testId="msg-unread" icon={Mail} label={t("admin.messages.stats.unread")} value={total} hint={t("admin.messages.stats.unread.hint")} accent="fog" />
      </section>

      {/* Search */}
      <div className="mb-5 flex items-center gap-3 border border-gold/20 bg-white/[0.02] px-4 py-2.5">
        <Search size={14} strokeWidth={1.5} className="text-bone/50" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="messages-search"
          placeholder={t("admin.messages.search")}
          className="flex-1 bg-transparent text-[13px] text-bone focus:outline-none"
        />
      </div>

      {err && <p className="mb-4 text-sm text-red-400">{err}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-6">
        {/* List */}
        <div className="border border-gold/15 bg-white/[0.02]" data-testid="messages-list">
          {loading && <p className="p-5 text-sm text-bone/50">{t("admin.label.loading")}</p>}
          {!loading && filtered.length === 0 && (
            <p className="p-5 text-sm text-bone/50">{t("admin.messages.noneMatch")}</p>
          )}
          {filtered.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setSelected(m)}
              data-testid={`message-row-${m.id}`}
              className={`w-full text-left px-5 py-3 border-b border-white/5 last:border-b-0 transition-colors ${selected?.id === m.id ? "bg-gold/10" : "hover:bg-white/[0.03]"}`}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-[13px] text-bone font-medium truncate">{m.name || t("admin.messages.noName")}</p>
                <span className="text-[10px] text-bone/50 flex-shrink-0">{fmtDate(m.created_at)}</span>
              </div>
              <p className="mt-0.5 text-[11px] text-bone/50 truncate">{m.email}</p>
              <p className="mt-1 text-[12px] text-bone/70 line-clamp-2">{m.message}</p>
            </button>
          ))}
        </div>

        {/* Detail */}
        <aside className="border border-gold/15 bg-white/[0.02] p-5 md:p-6 min-h-[300px]" data-testid="messages-detail">
          {!selected && <p className="text-sm text-bone/50">{t("admin.messages.selectPrompt")}</p>}
          {selected && (
            <div>
              <p className="text-[10px] tracking-[0.28em] uppercase text-gold/70">{t("admin.messages.from")}</p>
              <p className="mt-1 font-serif text-lg text-bone">{selected.name || t("admin.messages.anonymous")}</p>
              <p className="text-[13px] text-bone/60">{selected.email}</p>
              <p className="mt-1 text-[10px] tracking-[0.24em] uppercase text-bone/40">
                {fmtDate(selected.created_at)} · {selected.lang?.toUpperCase() || "EN"}
              </p>
              <hr className="my-4 border-white/10" />
              <p className="text-[14px] text-bone/85 whitespace-pre-wrap leading-relaxed">
                {selected.message}
              </p>
            </div>
          )}
        </aside>
      </div>
    </AdminShell>
  );
}
